import React, { useState, useEffect } from 'react';
import { useAuth, USER_ROLES } from '../context/AuthContext';
import './UserManagement.css';

const UserManagement = () => {
  const { hasPermission } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',
    isActive: true,
    b2bSettings: {
      apiAccess: false,
      commission: 0,
      discountRate: 0
    }
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    // Simulate API call
    setTimeout(() => {
      const mockUsers = [
        {
          id: 1,
          name: 'Ahmet Yılmaz',
          email: 'ahmet@firma.com',
          phone: '0555 123 4567',
          role: USER_ROLES.ADMIN,
          isActive: true,
          createdAt: '2025-01-15',
          lastLogin: '2025-08-06 14:30'
        },
        {
          id: 2,
          name: 'Fatma Demir',
          email: 'fatma@firma.com',
          phone: '0555 234 5678',
          role: USER_ROLES.SALES,
          isActive: true,
          createdAt: '2025-02-20',
          lastLogin: '2025-08-06 12:15'
        },
        {
          id: 3,
          name: 'Mehmet Kaya',
          email: 'mehmet@firma.com',
          phone: '0555 345 6789',
          role: USER_ROLES.WAREHOUSE,
          isActive: true,
          createdAt: '2025-03-10',
          lastLogin: '2025-08-06 09:45'
        }
      ];
      setUsers(mockUsers);
      setLoading(false);
    }, 1000);
  };

  const handleAddUser = () => {
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      role: '',
      isActive: true,
      b2bSettings: {
        apiAccess: false,
        commission: 0,
        discountRate: 0
      }
    });
    setShowAddModal(true);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      phone: user.phone,
      role: user.role,
      isActive: user.isActive,
      b2bSettings: user.b2bSettings || {
        apiAccess: false,
        commission: 0,
        discountRate: 0
      }
    });
    setShowAddModal(true);
  };

  const handleSaveUser = () => {
    if (editingUser) {
      // Update existing user
      setUsers(users.map(user => 
        user.id === editingUser.id 
          ? { ...user, ...formData }
          : user
      ));
    } else {
      // Add new user
      const newUser = {
        id: Date.now(),
        ...formData,
        createdAt: new Date().toISOString().split('T')[0],
        lastLogin: null
      };
      setUsers([...users, newUser]);
    }
    setShowAddModal(false);
  };

  const handleDeleteUser = (userId) => {
    if (window.confirm('Bu kullanıcıyı silmek istediğinizden emin misiniz?')) {
      setUsers(users.filter(user => user.id !== userId));
    }
  };

  const getRoleDisplayName = (role) => {
    const roleNames = {
      [USER_ROLES.ADMIN]: 'Sistem Yöneticisi',
      [USER_ROLES.MANAGER]: 'Yönetici',
      [USER_ROLES.SALES]: 'Satış',
      [USER_ROLES.WAREHOUSE]: 'Depo',
      [USER_ROLES.CONSULTANT]: 'Danışman',
      [USER_ROLES.HR]: 'İK',
      [USER_ROLES.CUSTOMER_SERVICE]: 'Müşteri Hizmetleri',
      [USER_ROLES.PRODUCTION]: 'Üretim',
      [USER_ROLES.B2B_USER]: 'B2B Kullanıcısı',
      [USER_ROLES.B2C_USER]: 'B2C Kullanıcısı'
    };
    return roleNames[role] || role;
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Kullanıcılar yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="user-management">
      <div className="page-header">
        <h2>Kullanıcı Yönetimi</h2>
        <p>Firma kullanıcılarını yönetin ve yetkilendirin</p>
      </div>

      <div className="actions-bar">
        <button className="btn btn-primary" onClick={handleAddUser}>
          ➕ Yeni Kullanıcı Ekle
        </button>
        <button className="btn btn-success" onClick={loadUsers}>
          🔄 Yenile
        </button>
      </div>

      <div className="users-grid">
        {users.map((user) => (
          <div key={user.id} className="user-card">
            <div className="user-header">
              <div className="user-avatar">
                {user.name.charAt(0)}
              </div>
              <div className="user-info">
                <h3>{user.name}</h3>
                <span className={`role-badge ${user.role}`}>
                  {getRoleDisplayName(user.role)}
                </span>
              </div>
              <div className={`status-badge ${user.isActive ? 'active' : 'inactive'}`}>
                {user.isActive ? 'Aktif' : 'Pasif'}
              </div>
            </div>

            <div className="user-details">
              <div className="detail-row">
                <span className="label">E-posta:</span>
                <span className="value">{user.email}</span>
              </div>
              <div className="detail-row">
                <span className="label">Telefon:</span>
                <span className="value">{user.phone}</span>
              </div>
              <div className="detail-row">
                <span className="label">Kayıt Tarihi:</span>
                <span className="value">{user.createdAt}</span>
              </div>
              {user.lastLogin && (
                <div className="detail-row">
                  <span className="label">Son Giriş:</span>
                  <span className="value">{user.lastLogin}</span>
                </div>
              )}
            </div>

            {user.role === USER_ROLES.B2B_USER && user.b2bSettings && (
              <div className="b2b-settings">
                <h4>B2B Ayarları</h4>
                <div className="b2b-details">
                  <div className="detail-row">
                    <span className="label">API Erişimi:</span>
                    <span className="value">{user.b2bSettings.apiAccess ? 'Açık' : 'Kapalı'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Komisyon:</span>
                    <span className="value">%{user.b2bSettings.commission}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">İndirim Oranı:</span>
                    <span className="value">%{user.b2bSettings.discountRate}</span>
                  </div>
                </div>
              </div>
            )}

            <div className="user-actions">
              <button 
                className="btn btn-primary btn-sm"
                onClick={() => handleEditUser(user)}
              >
                ✏️ Düzenle
              </button>
              <button 
                className="btn btn-warning btn-sm"
                onClick={() => handleEditUser({...user, isActive: !user.isActive})}
              >
                {user.isActive ? '⏸️ Pasif Yap' : '▶️ Aktif Yap'}
              </button>
              <button 
                className="btn btn-danger btn-sm"
                onClick={() => handleDeleteUser(user.id)}
              >
                🗑️ Sil
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Add/Edit User Modal */}
      {showAddModal && (
        <div className="modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editingUser ? 'Kullanıcı Düzenle' : 'Yeni Kullanıcı Ekle'}</h3>
              <button className="close-btn" onClick={() => setShowAddModal(false)}>✕</button>
            </div>
            
            <form onSubmit={(e) => { e.preventDefault(); handleSaveUser(); }}>
              <div className="form-group">
                <label>Ad Soyad</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label>E-posta</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label>Telefon</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label>Rol</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                  required
                >
                  <option value="">Rol Seçin</option>
                  <option value={USER_ROLES.ADMIN}>Sistem Yöneticisi</option>
                  <option value={USER_ROLES.MANAGER}>Yönetici</option>
                  <option value={USER_ROLES.SALES}>Satış</option>
                  <option value={USER_ROLES.WAREHOUSE}>Depo</option>
                  <option value={USER_ROLES.CONSULTANT}>Danışman</option>
                  <option value={USER_ROLES.HR}>İK</option>
                  <option value={USER_ROLES.CUSTOMER_SERVICE}>Müşteri Hizmetleri</option>
                  <option value={USER_ROLES.PRODUCTION}>Üretim</option>
                  <option value={USER_ROLES.B2B_USER}>B2B Kullanıcısı</option>
                  <option value={USER_ROLES.B2C_USER}>B2C Kullanıcısı</option>
                </select>
              </div>

              {formData.role === USER_ROLES.B2B_USER && (
                <div className="b2b-settings-form">
                  <h4>B2B Ayarları</h4>
                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={formData.b2bSettings.apiAccess}
                        onChange={(e) => setFormData({
                          ...formData,
                          b2bSettings: {
                            ...formData.b2bSettings,
                            apiAccess: e.target.checked
                          }
                        })}
                      />
                      API Erişimi
                    </label>
                  </div>
                  <div className="form-group">
                    <label>Komisyon Oranı (%)</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={formData.b2bSettings.commission}
                      onChange={(e) => setFormData({
                        ...formData,
                        b2bSettings: {
                          ...formData.b2bSettings,
                          commission: parseFloat(e.target.value)
                        }
                      })}
                    />
                  </div>
                  <div className="form-group">
                    <label>İndirim Oranı (%)</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={formData.b2bSettings.discountRate}
                      onChange={(e) => setFormData({
                        ...formData,
                        b2bSettings: {
                          ...formData.b2bSettings,
                          discountRate: parseFloat(e.target.value)
                        }
                      })}
                    />
                  </div>
                </div>
              )}

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowAddModal(false)}>
                  İptal
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingUser ? 'Güncelle' : 'Kaydet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement; 